import { z } from 'zod';

import type {
  ShipmentLogisticsCaptureShellDto,
  ShipmentLogisticsCaptureShellMaterialRefDto,
} from './shipment-logistics-capture-shell.dto.js';
import { ShipmentMaterialRefSchema } from './shipment-weigh-evidence.schema.js';
import {
  IsoDateTimeSchema,
  RcScopeTypeSchema,
  ShipmentLogisticsCaptureShellStatusSchema,
} from './shipments-types.js';
import { OutboundRecordTypeSchema } from '../outbound/outbound-record-reference.schema.js';

const nullableNonNegativeNumberSchema = z.number().min(0).nullable();
const trimmedStringSchema = z.string().trim().min(1);

export const ShipmentLogisticsCaptureShellMaterialRefSchema: z.ZodType<ShipmentLogisticsCaptureShellMaterialRefDto> =
  z
    .object({
      shipmentMaterialRef: ShipmentMaterialRefSchema,
      materialLabel: trimmedStringSchema.nullable(),
      huExpectedPhysicalWeightLb: nullableNonNegativeNumberSchema,
      reportableWeightLb: nullableNonNegativeNumberSchema,
      minimumPaymentThresholdLb: nullableNonNegativeNumberSchema,
    })
    .strict();

export const ShipmentLogisticsCaptureShellSchema: z.ZodType<ShipmentLogisticsCaptureShellDto> = z
  .object({
    companyId: z.uuid(),
    shipmentId: z.uuid().nullable(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    shipmentReference: trimmedStringSchema,
    stationId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    rcScopeId: z.uuid(),
    materialId: z.uuid(),
    receiverLabel: trimmedStringSchema.nullable(),
    departedAt: IsoDateTimeSchema,
    status: ShipmentLogisticsCaptureShellStatusSchema,
    captureEligible: z.boolean(),
    secureLinkEligible: z.boolean(),
    activeDriverEmployeeIds: z.array(z.uuid()),
    materialRefs: z.array(ShipmentLogisticsCaptureShellMaterialRefSchema).nonempty(),
    fullShipmentLinkedAt: IsoDateTimeSchema.nullable(),
    reversedAt: IsoDateTimeSchema.nullable(),
    reversedByUserId: z.uuid().nullable(),
    reversalReasonCode: trimmedStringSchema.nullable(),
    reversalReasonText: trimmedStringSchema.nullable(),
    sourceCreatedAt: IsoDateTimeSchema.nullable(),
    sourceUpdatedAt: IsoDateTimeSchema.nullable(),
    lastSourceEventId: z.uuid().nullable(),
    lastSourceOccurredAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.outboundRecordType === 'SHIPMENT' && value.shipmentId !== value.outboundRecordId) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId must match outboundRecordId for SHIPMENT shells.',
        path: ['shipmentId'],
      });
    }

    if (value.outboundRecordType === 'COVER_SHIPMENT' && value.shipmentId !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'shipmentId must be null for COVER_SHIPMENT shells.',
        path: ['shipmentId'],
      });
    }
  });
